import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  api,
  markReconcileHandledByList,
  reconcileRequiredCount,
  type ApiError,
  type BatchWriteResult,
} from './client'

// M9 (Scheibe E): dieselbe Ergebnisform wie bei den Keyword-Batches, definiert in
// `client.ts` und hier namensgleich re-exportiert.
export type { BatchWriteFailedItem, BatchWriteResult } from './client'

// --- Types ---

export interface Campaign {
  id: number
  profileId: number
  amazonCampaignId: string
  adType: 'sp' | 'sb' | 'sd'
  name: string
  state: 'enabled' | 'paused' | 'archived'
  startDate: string | null
  endDate: string | null
  budget: string | null
  budgetType: 'daily' | 'lifetime' | null
  targetingType: 'auto' | 'manual' | null
  biddingStrategy: string | null
  role?: string | null
  parentId?: number | null
  createdAt: string
  updatedAt: string
  adGroupCount?: number
  // v24 KPIs (List-with-Metrics)
  impressions?: number
  clicks?: number
  spend?: number
  sales?: number
  orders?: number
  acos?: number | null
  roas?: number | null
}

export interface CampaignKpis {
  impressions: number
  clicks: number
  spend: number
  sales: number
  orders: number
}

export interface CampaignDetail extends Campaign {
  metrics7d: CampaignKpis
  metrics30d?: CampaignKpis
  parentInfo?: { id: number; amazonParentAsin: string; name: string; ppcCode: string | null } | null
}

export interface CampaignListResponse {
  campaigns: Campaign[]
  total: number
  limit: number
  offset: number
  dateFrom?: string
  dateTo?: string
}

export interface CampaignFilters {
  profileId?: number
  adType?: string
  state?: string
  search?: string
  limit?: number
  offset?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  dateFrom?: string
  dateTo?: string
}

// --- Hooks ---

export function useCampaigns(filters: CampaignFilters = {}, options: { enabled?: boolean } = {}) {
  const params = new URLSearchParams()
  if (filters.profileId) params.set('profileId', String(filters.profileId))
  if (filters.adType) params.set('adType', filters.adType)
  if (filters.state) params.set('state', filters.state)
  if (filters.search) params.set('search', filters.search)
  if (filters.limit) params.set('limit', String(filters.limit))
  // Explicit page 0 too: the request names the page instead of relying on the server default.
  if (filters.offset !== undefined) params.set('offset', String(filters.offset))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortDir) params.set('sortDir', filters.sortDir)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)

  const qs = params.toString()
  return useQuery({
    queryKey: ['campaigns', filters],
    // Read-only list: TanStack aborts the request once a newer search replaces this key.
    queryFn: ({ signal }) => api.get<CampaignListResponse>(`/campaigns/${qs ? `?${qs}` : ''}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

export function useCampaign(id: number | null) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.get<{ campaign: CampaignDetail }>(`/campaigns/${id}/`),
    enabled: id !== null,
  })
}

export function useUpdateCampaignState() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, state }: { id: number; state: string }) =>
      api.put(`/campaigns/${id}/state/`, { state }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
      qc.invalidateQueries({ queryKey: ['campaign'] })
    },
  })
}

export function useBatchCampaignState() {
  const qc = useQueryClient()
  // Fix-Runde 1 (F1): Der Hook wird IN `CampaignList` aufgerufen und teilt damit deren
  // Lebenszyklus. Nur solange die Liste lebt, kann sie den Hinweis auch zeigen —
  // sonst darf der globale Toast NICHT unterdrueckt werden (siehe unten).
  // `isMounted.current = true` steht bewusst im Effekt-RUMPF, nicht nur `false` im
  // Cleanup: Refs ueberleben in React StrictMode das simulierte Unmount/Remount des
  // Dev-Builds (main.tsx rendert unter StrictMode). Ein reiner Cleanup-Setter haette
  // den Hook danach dauerhaft fuer unmountet gehalten.
  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  return useMutation<BatchWriteResult, ApiError, { ids: number[]; state: string }>({
    mutationFn: async ({ ids, state }) => {
      try {
        return await api.post<BatchWriteResult>('/campaigns/batch/state/', { ids, state })
      } catch (err) {
        // Muss HIER stehen und nicht in `onError`: der globale MutationCache-Handler
        // (main.tsx) laeuft VOR jedem `onError`. `CampaignList` zeigt den Fall selbst an —
        // ABER nur, wenn es sie noch gibt. Ist der Operator waehrend des Writes
        // weggeblaettert, bleibt `toasted` unberuehrt und er bekommt den Toast. Ein
        // verschluckter Hinweis waere schlimmer als ein doppelter.
        if (isMounted.current) markReconcileHandledByList(err)
        throw err
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
    },
    onError: (err) => {
      // Ein 502 aus `ackOrReconcile` ist ein TEILergebnis: `result.success` Eintraege
      // sind geschrieben. Ohne Invalidierung zeigte die Liste weiter den alten Stand.
      if (reconcileRequiredCount(err) > 0) qc.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

export function useCreateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post('/campaigns/', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

export function useUpdateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Record<string, unknown>) =>
      api.put(`/campaigns/${id}/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
      qc.invalidateQueries({ queryKey: ['campaign'] })
    },
  })
}

// --- Pilot S1: Rename & Revert ---

export interface CampaignAlias {
  id: number
  campaignId: number
  oldName: string
  newName: string
  renamedAt: string
}

export function useCampaignAliases(campaignId: number | null) {
  return useQuery({
    queryKey: ['campaignAliases', campaignId],
    queryFn: () => api.get<{ aliases: CampaignAlias[] }>(`/campaigns/${campaignId}/aliases/`),
    enabled: campaignId !== null,
  })
}

export function useRenameCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name, expectedName }: { id: number; name: string; expectedName?: string }) =>
      api.put<{ campaign: CampaignDetail }>(`/campaigns/${id}/name/`, {
        name,
        ...(expectedName === undefined ? {} : { expectedName }),
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['campaigns'] }),
        qc.invalidateQueries({ queryKey: ['campaign', variables.id] }),
        qc.invalidateQueries({ queryKey: ['campaignAliases', variables.id] }),
        qc.invalidateQueries({ queryKey: ['naming', 'campaign-preview'] }),
      ])
    },
  })
}

export function useRevertCampaignName() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api.post<{ campaign: CampaignDetail }>(`/campaigns/${id}/revert-name/`, {}),
    onSuccess: async (_, variables) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['campaigns'] }),
        qc.invalidateQueries({ queryKey: ['campaign', variables.id] }),
        qc.invalidateQueries({ queryKey: ['campaignAliases', variables.id] }),
        qc.invalidateQueries({ queryKey: ['naming', 'campaign-preview'] }),
      ])
    },
  })
}

// --- Ad Groups ---

export interface AdGroup {
  id: number
  campaignId: number
  amazonAdGroupId: string
  name: string
  state: 'enabled' | 'paused' | 'archived'
  defaultBid: string | null
  keywordCount?: number
  createdAt: string
  updatedAt: string
}

export function useAdGroups(campaignId: number | null, query: { limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams()
  if (query.limit) params.set('limit', String(query.limit))
  if (query.offset) params.set('offset', String(query.offset))
  const qs = params.toString()

  return useQuery({
    queryKey: ['adGroups', campaignId, query],
    queryFn: () => api.get<{ adGroups: AdGroup[]; total: number }>(`/campaigns/${campaignId}/ad-groups/${qs ? `?${qs}` : ''}`),
    enabled: campaignId !== null,
  })
}
